import { useState, useEffect, useMemo } from 'react';
import { Settings, Save, X, CreditCard as Edit2, Loader2 } from 'lucide-react';
import { apiClient } from '../lib/apiClient';
import { ReportStatus, StatusType } from '../types/inspection';
import { DataTable, type ColumnDef } from './DataTable';
import { logAudit, AUDIT_ACTIONS, AUDIT_ENTITIES, computeChanges } from '../lib/auditLog';

const STATUS_TYPE_DESCRIPTIONS: Record<StatusType, string> = {
  draft: 'In Progress',
  in_progress: 'In Progress',
  submitted: 'Technician Submission',
  work_order: 'Manager to Account Manager',
  complete: 'Completed (Closed)',
  rejected: 'Rejected',
  approved: 'Approved',
};

export function ReportStatusSettings() {
  const [statuses, setStatuses] = useState<ReportStatus[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ label: '', color: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void loadStatuses();
  }, []);

  async function loadStatuses() {
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.get<ReportStatus[]>('/api/report-statuses');
      const sorted = [...data].sort((a, b) => a.order_index - b.order_index);
      setStatuses(sorted);
    } catch (err) {
      console.error('Error loading statuses:', err);
      setError('Failed to load status settings');
    } finally {
      setLoading(false);
    }
  }

  function startEdit(status: ReportStatus) {
    setEditingId(status.id);
    setEditForm({ label: status.label, color: status.color });
  }

  function cancelEdit() {
    setEditingId(null);
    setEditForm({ label: '', color: '' });
  }

  async function saveEdit(id: string) {
    try {
      setSaving(true);
      setError(null);
      const original = statuses.find((s) => s.id === id);
      await apiClient.put(`/api/report-statuses/${id}`, {
        status_type: original?.status_type,
        label: editForm.label,
        color: editForm.color,
        order_index: original?.order_index,
      });
      await logAudit({
        action: AUDIT_ACTIONS.UPDATE,
        entityType: AUDIT_ENTITIES.REPORT_STATUS,
        entityId: id,
        entityLabel: original?.status_type || '',
        details: computeChanges(
          { label: original?.label, color: original?.color },
          { label: editForm.label, color: editForm.color },
        ),
        pageContext: 'report-status',
      });
      setStatuses(statuses.map((s) => (s.id === id ? { ...s, label: editForm.label, color: editForm.color } : s)));
      cancelEdit();
    } catch (err) {
      console.error('Error saving status:', err);
      setError('Failed to save changes');
    } finally {
      setSaving(false);
    }
  }

  const columns: ColumnDef<ReportStatus>[] = useMemo(() => [
    {
      key: 'status_type',
      label: 'Status Type',
      render: (status) => (
        <span className="text-sm font-medium text-theme-primary">{status.status_type}</span>
      ),
    },
    {
      key: 'description',
      label: 'Description',
      headerClassName: 'hidden md:table-cell',
      cellClassName: 'hidden md:table-cell',
      render: (status) => (
        <span className="text-sm text-theme-secondary">{STATUS_TYPE_DESCRIPTIONS[status.status_type] ?? ''}</span>
      ),
    },
    {
      key: 'label',
      label: 'Label',
      render: (status) => (
        <span className="text-sm text-theme-primary">{status.label}</span>
      ),
    },
    {
      key: 'color',
      label: 'Color',
      headerClassName: 'hidden sm:table-cell',
      cellClassName: 'hidden sm:table-cell',
      render: (status) => (
        <span className="text-sm font-mono text-theme-secondary">{status.color}</span>
      ),
    },
    {
      key: 'preview',
      label: 'Preview',
      render: (status) => (
        <span
          className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium text-white"
          style={{ backgroundColor: status.color }}
        >
          {status.label}
        </span>
      ),
    },
    {
      key: 'actions',
      label: '',
      headerClassName: 'w-12 text-right',
      render: (status) => (
        <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => startEdit(status)}
            className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-500/10 rounded-md transition-colors"
            title="Edit"
          >
            <Edit2 size={16} />
          </button>
        </div>
      ),
    },
  ], []);

  const renderExpandedRow = (status: ReportStatus) => {
    if (editingId !== status.id) return null;
    return (
      <div className="bg-subtle rounded-lg border border-line p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-sm font-semibold text-theme-primary">Edit "{status.status_type}" Status</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-theme-secondary mb-1">Label</label>
            <input
              type="text"
              value={editForm.label}
              onChange={(e) => setEditForm({ ...editForm, label: e.target.value })}
              className="w-full px-3 py-2 border border-line bg-surface text-theme-primary rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Label"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-theme-secondary mb-1">Color</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={editForm.color}
                onChange={(e) => setEditForm({ ...editForm, color: e.target.value })}
                className="h-9 w-14 border border-line rounded cursor-pointer"
              />
              <input
                type="text"
                value={editForm.color}
                onChange={(e) => setEditForm({ ...editForm, color: e.target.value })}
                className="flex-1 px-3 py-2 border border-line bg-surface text-theme-primary rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="#000000"
              />
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-theme-secondary">Preview:</span>
          <span
            className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium text-white"
            style={{ backgroundColor: editForm.color }}
          >
            {editForm.label}
          </span>
        </div>
        <div className="flex gap-2 pt-1">
          <button
            onClick={() => saveEdit(status.id)}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save
          </button>
          <button
            onClick={cancelEdit}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-subtle text-theme-secondary rounded-lg hover:bg-subtle transition-colors text-sm font-medium disabled:opacity-50"
          >
            <X size={14} />
            Cancel
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto h-full flex flex-col overflow-hidden">
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <div className="flex items-center gap-2">
          <div>
            <div className="flex items-center gap-2">
              <Settings className="w-6 h-6 text-blue-600" />
              <h1 className="text-2xl font-bold text-theme-primary">Report Status Settings</h1>
            </div>
            <p className="text-sm text-theme-secondary mt-1">Configure the labels and colors for inspection report statuses</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-3 p-3 bg-red-50 border border-red-200 dark:bg-red-500/10 dark:border-red-500/30 rounded-lg text-sm text-red-700 dark:text-red-300 flex-shrink-0">
          {error}
        </div>
      )}

      <DataTable
        data={statuses}
        columns={columns}
        keyField="id"
        loading={loading}
        emptyIcon={<Settings size={40} className="text-theme-secondary" />}
        emptyTitle="No statuses configured"
        onRowClick={(status) => {
          if (editingId === status.id) {
            cancelEdit();
          } else {
            startEdit(status);
          }
        }}
        expandedId={editingId}
        expandedRow={renderExpandedRow}
      />

      <div className="mt-3 p-3 bg-blue-50 border border-blue-200 dark:bg-blue-500/10 dark:border-blue-500/30 rounded-lg flex-shrink-0">
        <p className="text-sm text-blue-800 dark:text-blue-300">
          <strong>Note:</strong> Status types are system-defined and cannot be changed. You can customize the labels and colors to match your workflow.
        </p>
      </div>
    </div>
  );
}
